/* ============================================================================
 *  Modèles de vue (view-models) de l'espace connecté + placeholder de chargement.
 * ----------------------------------------------------------------------------
 *  Les types ci-dessous sont les view-models consommés par les vues et produits
 *  par les mappers (src/mappers/*) à partir des DTO de l'API. `BLANK_MEMBER` est
 *  un placeholder neutre utilisé par `useAuth` le temps que `GET /me` réponde
 *  (évite un flash « non connecté »), avant que le profil réel ne l'écrase.
 *
 *  (Ancien fichier de seed de démonstration : les données fictives — adhérent,
 *  contrats, RIB, carte, documents — ont été retirées une fois l'espace
 *  connecté câblé au back-end.)
 * ========================================================================== */

/** Famille du contrat — pilote les onglets « Santé / Assurance » de Mes contrats. */
export type ContractCategory = 'sante' | 'assurance'
/** Statut du contrat — clé i18n : auth.account.status.<status>. */
export type ContractStatus = 'active' | 'pending' | 'terminating' | 'retracting' | 'terminated'

/** Ligne de garantie d'un contrat (libellé + niveau de prise en charge). */
export interface ContractGuarantee {
  label: string
  value: string
}

/** Bloc « Informations générales » d'un contrat. */
export interface ContractGeneralInfo {
  /** Mode de paiement localisé (ex. « Prélèvement »). */
  paymentMode: string
  /** Rythme de paiement localisé (ex. « Mensuel »). */
  paymentRhythm: string
  /** Montant du paiement, déjà formaté (ex. « 10,50 €/mois »). */
  paymentAmount: string
  /** Date de début (ISO 8601). */
  startDate: string
  /** Date de fin (ISO 8601) ou null si toujours en cours. */
  endDate: string | null
  /** Durée du contrat, en mois. */
  durationMonths: number
  /** Tacite reconduction. */
  tacitRenewal: boolean
}

export interface MemberContract {
  /** Libellé du produit souscrit. */
  name: string
  /** Référence (numéro de contrat). */
  reference: string
  /** Famille du contrat (onglets santé / assurance). */
  category: ContractCategory
  /** Statut du contrat — clé i18n : auth.account.status.<status>. */
  status: ContractStatus
  /** Date de souscription (ISO 8601) — base du délai légal de rétractation (14 j). */
  subscribedOn: string
  /** Informations générales affichées dans le détail du contrat. */
  general: ContractGeneralInfo
  /** Garanties du contrat. */
  guarantees: ContractGuarantee[]
  /** Link to the Calaméo guarantee sheet, or null when the product publishes
   *  none — the link is then hidden (SI-1455). */
  guaranteeSheetUrl: string | null
}

export interface MemberAddress {
  line1: string
  /** Complément d'adresse (bâtiment, étage, appartement…). */
  line2: string
  postalCode: string
  city: string
  /** Code pays ISO 3166-1 alpha-2 (ex. « FR »). */
  country: string
}

/** Famille d'un document — pilote les onglets de Mes documents. */
export type DocumentCategory = 'identite' | 'etude' | 'justificatif' | 'remboursement'

/** Document de l'adhérent (issu de GET /me). */
export interface MemberDocument {
  /** Intitulé du document. */
  title: string
  /** Nom du fichier. */
  fileName: string
  /** Catégorie (onglets identité / étude / justificatif / remboursement). */
  category: DocumentCategory
  /** Date d'ajout (ISO 8601). */
  addedAt: string
  /** URL d'aperçu (inline), si fournie par l'API. */
  url?: string
  /** URL de téléchargement (pièce jointe), si fournie par l'API. */
  downloadUrl?: string
}

/** Statut d'un remboursement — clé i18n : auth.account.reimbursements.status.<status>. */
export type ReimbursementStatus = 'total' | 'partial'

/** Acte élémentaire d'un soin (un `care_item` du décompte). */
export interface ReimbursementAct {
  /** Nature de l'acte (ex. « Couronne »). */
  label: string
  /** Montant facturé pour cet acte. */
  cost: number
  /** Part prise en charge par la sécurité sociale (régime obligatoire). */
  socialSecurity: number
  /** Part prise en charge par Smerra (régime complémentaire). */
  smerra: number
  /** Statut de l'acte : un acte peut être intégralement couvert au sein d'un
   *  décompte partiellement remboursé. */
  status: ReimbursementStatus
}

/** Remboursement de santé — une ligne du relevé (une opération
 *  `kind: refund` / `category: care`).
 *  Les montants viennent du back-end ; seules les valeurs dérivées (total
 *  remboursé, reste à charge) sont calculées dans `useReimbursements`. */
export interface Reimbursement {
  id: string
  /** Date du soin (ISO 8601). */
  careDate: string
  /** Nature du soin ; vide quand le décompte ne porte aucun acte. */
  label: string
  /** Montant facturé. */
  cost: number
  /** Part sécurité sociale. */
  socialSecurity: number
  /** Part Smerra. */
  smerra: number
  status: ReimbursementStatus
  /** Actes du décompte ; plusieurs pour un multi-actes, parfois aucun. */
  acts: ReimbursementAct[]
}

/** Profil de l'adhérent affiché dans l'espace connecté (alimenté par GET /me). */
export interface Member {
  /** UUID backend de l'utilisateur — sert à s'identifier comme souscripteur
   *  connecté dans un contract_draft (voir useSubscription/useContractDraft). */
  id: string
  /** Civilité — détermine l'accord « Né(e) le ». */
  civility: 'mr' | 'mrs'
  firstName: string
  lastName: string
  email: string
  /** Numéro d'adhérent. */
  memberNumber: string
  /** Date de naissance (ISO 8601). */
  birthDate: string
  /** Numéro de sécurité sociale (NIR). */
  nir: string
  phone: string
  address: MemberAddress
}

/** Placeholder neutre, le temps que GET /me réponde (puis remplacé par le profil réel). */
export const BLANK_MEMBER: Member = {
  id: '',
  civility: 'mr',
  firstName: '',
  lastName: '',
  email: '',
  memberNumber: '',
  birthDate: '',
  nir: '',
  phone: '',
  address: { line1: '', line2: '', postalCode: '', city: '', country: '' },
}
